import { useState, useEffect } from 'react';
import { api } from '@/lib/api-client';
import { 
  Search, BookOpen, ChevronRight, ArrowLeft, 
  Rocket, Server, Shield, CreditCard, Terminal, 
  Users, Zap, HelpCircle, FileText, Code,
  Copy, Check, ExternalLink
} from 'lucide-react';

// Comprehensive documentation content with proper markdown-style formatting
const docContents = {
  'Quick Start Guide': {
    icon: Rocket,
    readTime: '5 min',
    content: `
# Quick Start Guide

Welcome to ClawStinger! This guide will help you deploy your first AI agent in under 60 seconds.

## Prerequisites

Before you begin, make sure you have:
- An email address for account registration
- An API key from your preferred LLM provider (Anthropic, OpenAI, or Google)
- A messaging platform account (Telegram, Discord, Slack, or WhatsApp)

## Step 1: Create Your Account

1. Visit [clawstinger.com/signup](/signup)
2. Enter your email and create a secure password
3. Verify your email address
4. Complete your profile setup

## Step 2: Choose Your Plan

All plans include a **14-day free trial** with full access to features:

| Plan | Price | Best For |
|------|-------|----------|
| Starter | $12/mo | Personal use, 1 agent |
| Professional | $29/mo | Teams, up to 10 agents |
| Enterprise | $69/mo | Agencies, up to 20 agents |

## Step 3: Create Your First Instance

1. Navigate to **Dashboard → Create Instance**
2. Choose a template (or start blank)
3. Select your hardware tier
4. Configure your AI model and messaging channel
5. Click **Deploy** — your agent will be live in ~60 seconds!

## Step 4: Configure Your Agent

After deployment:
- Set up your agent's persona and system prompt
- Add API keys for your LLM provider
- Install skills from the marketplace
- Configure messaging integrations

## Next Steps

- [Creating Your First Instance](/docs/creating-first-instance)
- [RDP Connection Guide](/docs/rdp-guide)
- [API Authentication](/docs/authentication)

---

**Need help?** Join our [Discord community](https://discord.gg/clawstinger) or [contact support](/contact).
    `
  },
  'System Requirements': {
    icon: Server,
    readTime: '3 min',
    content: `
# System Requirements

ClawStinger is a fully managed cloud service — no local installation required.

## Browser Requirements

| Browser | Minimum Version |
|---------|-----------------|
| Chrome | 90+ |
| Firefox | 88+ |
| Safari | 14+ |
| Edge | 90+ |

## For RDP Access

To connect via RDP, you can use:

### Browser-Based (Recommended)
- No additional software needed
- Works in any modern browser
- Uses Apache Guacamole

### Native RDP Client
- **Windows**: Microsoft Remote Desktop (built-in)
- **macOS**: Microsoft Remote Desktop from App Store
- **Linux**: Remmina, FreeRDP, or Vinagre

## API Requirements

For API integration:
- HTTPS support (TLS 1.2+)
- JSON parsing capability
- Bearer token authentication support

## Network Requirements

- Outbound HTTPS (port 443)
- WebSocket support for real-time features
- Stable internet connection (5+ Mbps recommended)

## LLM Provider Requirements

You'll need an API key from at least one provider:

| Provider | Get API Key |
|----------|-------------|
| Anthropic | [console.anthropic.com](https://console.anthropic.com) |
| OpenAI | [platform.openai.com](https://platform.openai.com) |
| Google AI | [makersuite.google.com](https://makersuite.google.com) |
    `
  },
  'Creating Your First Instance': {
    icon: Zap,
    readTime: '8 min',
    content: `
# Creating Your First Instance

This guide walks you through the 5-step instance creation wizard.

## Step 1: Choose a Template

Select from pre-configured templates or start fresh:

| Template | Description | Includes |
|----------|-------------|----------|
| **Blank** | Empty configuration | Nothing pre-configured |
| **Personal Assistant** | General-purpose helper | Calendar, reminders, web search |
| **Customer Support** | Handle support tickets | FAQ responses, ticket creation |
| **Dev Assistant** | Developer productivity | Code review, documentation, debugging |

## Step 2: Select Hardware Tier

Choose based on your expected workload:

### Starter ($12/month)
- 2 vCPU cores
- 4 GB RAM
- 50 GB NVMe SSD
- Best for: Single agent, light usage

### Professional ($29/month)
- 4 vCPU cores
- 8 GB RAM
- 100 GB NVMe SSD
- Best for: Multiple agents, moderate usage

### Enterprise ($69/month)
- 8 vCPU cores
- 16 GB RAM
- 200 GB NVMe SSD
- Best for: High-volume, production workloads

## Step 3: Select Region

Choose the datacenter closest to your users:

- 🇺🇸 US East (Virginia)
- 🇺🇸 US West (Oregon)
- 🇪🇺 Europe (Frankfurt)
- 🇸🇬 Asia Pacific (Singapore)

## Step 4: Configure Instance

### Basic Settings
- **Instance Name**: A memorable name (e.g., "Support Bot")
- **Description**: Optional notes for your reference

### AI Configuration
- **LLM Provider**: Anthropic, OpenAI, or Google
- **Model**: Select specific model version
- **API Key**: Your provider's API key

### Messaging Channel
- Telegram
- Discord
- Slack
- WhatsApp

## Step 5: Review & Deploy

Verify your configuration and click **Deploy**.

Deployment typically completes in **30-60 seconds**.

---

**Tip**: You can modify all settings after deployment from the instance details page.
    `
  },
  'Hardware Tiers': {
    icon: Server,
    readTime: '4 min',
    content: `
# Hardware Tiers

ClawStinger offers three hardware tiers optimized for different use cases.

## Tier Comparison

| Feature | Starter | Professional | Enterprise |
|---------|---------|--------------|------------|
| **Price** | $12/mo | $29/mo | $69/mo |
| **vCPU** | 2 cores | 4 cores | 8 cores |
| **RAM** | 4 GB | 8 GB | 16 GB |
| **Storage** | 50 GB NVMe | 100 GB NVMe | 200 GB NVMe |
| **Max Agents** | 1 | 10 | 20 |
| **Bandwidth** | 1 TB | 3 TB | 10 TB |

## All Tiers Include

✓ Full RDP/SSH access
✓ Daily automated backups
✓ Pre-installed integrations
✓ 99.9% uptime SLA
✓ 24/7 monitoring
✓ DDoS protection

## Choosing the Right Tier

### Starter is best for:
- Personal projects
- Testing and development
- Single-agent deployments
- Light message volumes (<1000/day)

### Professional is best for:
- Small teams
- Multiple agents
- Moderate message volumes
- Customer-facing bots

### Enterprise is best for:
- Agencies managing client bots
- High-volume production use
- Mission-critical applications
- Complex multi-agent workflows

## Upgrading

You can upgrade your tier anytime from **Instance Settings → Hardware**.

Upgrades are instant with zero downtime. Downgrades take effect at the next billing cycle.
    `
  },
  'RDP Connection Guide': {
    icon: Terminal,
    readTime: '6 min',
    content: `
# RDP Connection Guide

Access your ClawStinger instance via Remote Desktop Protocol (RDP).

## Method 1: Browser-Based (Recommended)

The easiest way to connect — no software installation required.

### Steps:
1. Go to **Dashboard → Instances → [Your Instance]**
2. Click the **RDP Access** tab
3. Click **"Connect via Browser"**
4. Wait for the Guacamole client to load
5. You're connected!

### Features:
- Works in any modern browser
- Clipboard sharing
- File transfer support
- Multi-monitor support

## Method 2: Native RDP Client

For better performance, use a native RDP client.

### Windows
1. Download the \`.rdp\` file from the RDP Access page
2. Double-click to open with Remote Desktop Connection
3. Click **Connect** and enter your credentials

### macOS
1. Install [Microsoft Remote Desktop](https://apps.apple.com/app/microsoft-remote-desktop/id1295203466) from the App Store
2. Click **Add PC** and enter the hostname from the RDP Access page
3. Enter your credentials and connect

### Linux
1. Install Remmina: \`sudo apt install remmina remmina-plugin-rdp\`
2. Create a new RDP connection
3. Enter hostname, username, and password from the RDP Access page

## Connection Details

Find these on your instance's RDP Access page:

| Field | Example |
|-------|---------|
| Hostname | \`inst-abc123.clawstinger.com\` |
| Port | \`3389\` |
| Username | \`clawuser\` |
| Password | (shown on page) |

## Security Best Practices

### Enable IP Whitelisting
Restrict RDP access to specific IP addresses:
1. Go to **Instance Settings → Security**
2. Enable **IP Whitelisting**
3. Add your IP addresses

### Use Strong Passwords
- Minimum 12 characters
- Mix of letters, numbers, symbols
- Change regularly

### Enable 2FA
Two-factor authentication adds an extra security layer for dashboard access.
    `
  },
  'Authentication': {
    icon: Shield,
    readTime: '5 min',
    content: `
# API Authentication

ClawStinger uses JWT (JSON Web Token) bearer authentication for API access.

## Getting a Token

### Login Endpoint

\`\`\`http
POST /api/auth/login
Content-Type: application/json

{
  "email": "your@email.com",
  "password": "your-password"
}
\`\`\`

### Response

\`\`\`json
{
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "user": {
    "id": "uuid",
    "email": "your@email.com",
    "full_name": "Your Name"
  }
}
\`\`\`

## Using the Token

Include the token in all API requests:

\`\`\`http
GET /api/instances
Authorization: Bearer eyJhbGciOiJIUzI1NiIs...
\`\`\`

## Token Expiration

- Access tokens expire after **24 hours**
- Use the refresh endpoint to get a new token
- Store tokens securely (never in localStorage for sensitive apps)

## Rate Limits

| Plan | Requests per Minute |
|------|---------------------|
| Starter | 100 |
| Professional | 300 |
| Enterprise | 500 |

Exceeding limits returns \`429 Too Many Requests\`.

## OAuth Authentication

ClawStinger also supports OAuth login:

### Google
\`\`\`
GET /api/auth/google/login
\`\`\`

### GitHub
\`\`\`
GET /api/auth/github/login
\`\`\`

## Security Headers

All API responses include:
- \`X-Content-Type-Options: nosniff\`
- \`X-Frame-Options: DENY\`
- \`Strict-Transport-Security: max-age=31536000\`
    `
  },
  'Billing & Payments': {
    icon: CreditCard,
    readTime: '4 min',
    content: `
# Billing & Payments

Manage your subscription, view invoices, and understand pricing.

## Payment Methods

We accept:
- Credit/Debit Cards (Visa, Mastercard, Amex)
- PayPal
- Wire Transfer (Enterprise only)

## Billing Cycle

- Monthly plans bill on the same day each month
- Annual plans bill once per year (17% discount)
- Pro-rated charges for mid-cycle upgrades

## View Invoices

1. Go to **Dashboard → Billing**
2. Click **Invoices** tab
3. Download PDF invoices for any period

## Credit System

ClawStinger uses a credit system for usage-based features:

| Feature | Credit Cost |
|---------|-------------|
| Additional storage (10 GB) | 5 credits |
| Extra bandwidth (100 GB) | 10 credits |
| Priority support ticket | 2 credits |

Purchase credits from **Billing → Add Credits**.

## Cancellation

- Cancel anytime from **Billing → Subscription**
- Access continues until end of billing period
- No refunds for partial months
- Data retained for 30 days after cancellation

## Enterprise Billing

Enterprise customers receive:
- Custom invoicing (NET 30)
- Volume discounts
- Dedicated account manager
- SLA guarantees
    `
  },
  'API Reference': {
    icon: Code,
    readTime: '10 min',
    content: `
# API Reference

Complete reference for the ClawStinger REST API.

## Base URL

\`\`\`
https://api.clawstinger.com/api
\`\`\`

## Endpoints

### Instances

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/instances\` | List all instances |
| POST | \`/instances\` | Create new instance |
| GET | \`/instances/{id}\` | Get instance details |
| PUT | \`/instances/{id}\` | Update instance |
| DELETE | \`/instances/{id}\` | Delete instance |
| POST | \`/instances/{id}/start\` | Start instance |
| POST | \`/instances/{id}/stop\` | Stop instance |
| POST | \`/instances/{id}/restart\` | Restart instance |

### Authentication

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | \`/auth/register\` | Register new user |
| POST | \`/auth/login\` | Login |
| GET | \`/auth/me\` | Get current user |
| PUT | \`/auth/me\` | Update profile |
| POST | \`/auth/forgot-password\` | Request password reset |

### Billing

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/billing/overview\` | Billing summary |
| GET | \`/billing/pricing\` | Get pricing plans |
| GET | \`/billing/invoices\` | List invoices |
| GET | \`/billing/credits\` | Credit balance |

### Skills

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/skills\` | List available skills |
| POST | \`/skills/{id}/install\` | Install skill |
| DELETE | \`/skills/{id}/uninstall\` | Uninstall skill |

## Response Format

All responses are JSON:

\`\`\`json
{
  "data": { ... },
  "meta": {
    "page": 1,
    "total": 100
  }
}
\`\`\`

## Error Codes

| Code | Description |
|------|-------------|
| 400 | Bad Request - Invalid parameters |
| 401 | Unauthorized - Invalid/missing token |
| 403 | Forbidden - Insufficient permissions |
| 404 | Not Found - Resource doesn't exist |
| 429 | Too Many Requests - Rate limited |
| 500 | Server Error - Contact support |
    `
  },
  'Troubleshooting': {
    icon: HelpCircle,
    readTime: '6 min',
    content: `
# Troubleshooting

Common issues and their solutions.

## Connection Issues

### Can't connect via RDP

**Symptoms**: RDP connection times out or fails

**Solutions**:
1. Check if instance is running (Dashboard → Instances)
2. Verify your IP is whitelisted (if enabled)
3. Try browser-based RDP instead
4. Check your firewall settings
5. Contact support if issue persists

### API returns 401 Unauthorized

**Symptoms**: API calls fail with 401 error

**Solutions**:
1. Check if your token has expired (24h limit)
2. Verify the token is included in the Authorization header
3. Ensure the header format is \`Bearer <token>\`
4. Re-login to get a fresh token

## Instance Issues

### Instance won't start

**Symptoms**: Instance stuck in "Starting" state

**Solutions**:
1. Wait 2-3 minutes (cold starts can be slow)
2. Check your billing status
3. Try stopping and restarting
4. Contact support if issue persists

### High CPU/Memory usage

**Symptoms**: Instance running slowly

**Solutions**:
1. Check which processes are consuming resources
2. Restart the instance
3. Consider upgrading to a higher tier
4. Optimize your agent's configuration

## Billing Issues

### Payment failed

**Symptoms**: Subscription not renewed

**Solutions**:
1. Update payment method in Billing settings
2. Check for expired cards
3. Verify sufficient funds
4. Try a different payment method

### Missing invoice

**Symptoms**: Can't find invoice for a period

**Solutions**:
1. Check email spam folder
2. Go to Billing → Invoices → Download
3. Contact billing@clawstinger.com

## Still Need Help?

- **Discord**: [discord.gg/clawstinger](https://discord.gg/clawstinger)
- **Email**: support@clawstinger.com
- **Tickets**: Dashboard → Support → New Ticket
    `
  },
  'FAQ': {
    icon: FileText,
    readTime: '5 min',
    content: `
# Frequently Asked Questions

Quick answers to common questions.

## General

### What is ClawStinger?
ClawStinger is a managed hosting platform for OpenClaw AI agents. We handle all the infrastructure so you can focus on building your AI assistant.

### Do I need technical experience?
No! ClawStinger is designed for everyone. Our wizard-based setup requires no coding or DevOps knowledge.

### What's included in the free trial?
Full access to all features for 14 days. No credit card required to start.

## Pricing

### Can I change plans later?
Yes, upgrade anytime. Downgrades take effect at the next billing cycle.

### Is there a discount for annual billing?
Yes, save 17% with annual billing on all plans.

### Do you offer refunds?
We offer a 14-day free trial. After that, we don't offer refunds for partial months.

## Technical

### What LLM providers do you support?
Anthropic (Claude), OpenAI (GPT), and Google AI (Gemini).

### Can I use my own API keys?
Yes, you must provide your own LLM API keys.

### Is my data secure?
Yes. We use encrypted connections, isolated instances, and don't store conversation data.

### What's the uptime SLA?
99.9% uptime guaranteed on all plans.

## Support

### How do I get help?
- Documentation (you're here!)
- Discord community
- Email support
- In-dashboard tickets

### What are support hours?
Email: 24/7 (response within 24h)
Priority: Business hours (response within 4h)
Enterprise: 24/7 with SLA
    `
  }
};

// Icon mapping for sections
const sectionIcons = {
  'Getting Started': Rocket,
  'Instance Management': Server,
  'Security & Auth': Shield,
  'Billing': CreditCard,
  'API': Code,
  'Support': HelpCircle
};

export default function Docs() {
  const [sections, setSections] = useState([]);
  const [search, setSearch] = useState('');
  const [activeDoc, setActiveDoc] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => { 
    api.getDocsSections().then(setSections); 
  }, []);

  const filtered = sections.filter(s => 
    !search || 
    s.title.toLowerCase().includes(search.toLowerCase()) || 
    s.items.some(i => i.toLowerCase().includes(search.toLowerCase()))
  );

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Render markdown-style content professionally
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let codeLanguage = '';
    let inTable = false;
    let tableRows = [];
    let tableHeader = [];
    let codeBlockId = 0;

    lines.forEach((line, index) => {
      // Code block handling
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          const code = codeContent.join('\n');
          const currentId = codeBlockId++;
          elements.push(
            <div key={`code-${index}`} className="my-4 rounded-lg overflow-hidden border border-zinc-800">
              <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
                <span className="text-xs font-mono text-zinc-500">{codeLanguage || 'code'}</span>
                <button
                  onClick={() => copyToClipboard(code, currentId)}
                  className="flex items-center gap-1 text-xs text-zinc-500 hover:text-emerald-400 transition-colors"
                >
                  {copiedCode === currentId ? <Check size={14} /> : <Copy size={14} />}
                  {copiedCode === currentId ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="p-4 bg-[#0A0A0A] overflow-x-auto">
                <code className="text-sm font-mono text-zinc-300">{code}</code>
              </pre>
            </div>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
          codeLanguage = line.trim().slice(3);
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Table handling
      if (line.includes('|') && line.trim().startsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableHeader = line.split('|').filter(c => c.trim()).map(c => c.trim());
          return;
        }
        if (line.includes('---')) return; // Skip separator
        tableRows.push(line.split('|').filter(c => c.trim()).map(c => c.trim()));
        return;
      } else if (inTable) {
        elements.push(
          <div key={`table-${index}`} className="my-6 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-zinc-900">
                  {tableHeader.map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-zinc-300 border border-zinc-800">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-zinc-900/50">
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-4 py-3 text-sm text-zinc-400 border border-zinc-800">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        inTable = false;
        tableRows = [];
        tableHeader = [];
      }

      // Headers
      if (line.startsWith('# ')) {
        elements.push(<h1 key={index} className="text-3xl font-bold text-white mt-8 mb-4 first:mt-0">{line.slice(2)}</h1>);
        return;
      }
      if (line.startsWith('## ')) {
        elements.push(<h2 key={index} className="text-xl font-semibold text-white mt-8 mb-3 pb-2 border-b border-zinc-800">{line.slice(3)}</h2>);
        return;
      }
      if (line.startsWith('### ')) {
        elements.push(<h3 key={index} className="text-lg font-semibold text-emerald-400 mt-6 mb-2">{line.slice(4)}</h3>);
        return;
      }

      // Horizontal rule
      if (line.trim() === '---') {
        elements.push(<hr key={index} className="my-8 border-zinc-800" />);
        return;
      }

      // Empty line
      if (line.trim() === '') {
        elements.push(<div key={index} className="h-4" />);
        return;
      }

      // List items
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        const content = line.trim().slice(2);
        elements.push(
          <div key={index} className="flex items-start gap-3 ml-4 my-1">
            <span className="text-emerald-400 mt-1.5">•</span>
            <span className="text-zinc-400 leading-relaxed">{renderInlineMarkdown(content)}</span>
          </div>
        );
        return;
      }

      // Numbered list
      if (/^\d+\.\s/.test(line.trim())) {
        const num = line.match(/^(\d+)\./)[1];
        const content = line.replace(/^\d+\.\s/, '');
        elements.push(
          <div key={index} className="flex items-start gap-3 ml-4 my-2">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold flex items-center justify-center">{num}</span>
            <span className="text-zinc-400 leading-relaxed">{renderInlineMarkdown(content)}</span>
          </div>
        );
        return;
      }

      // Checkmark items
      if (line.trim().startsWith('✓')) {
        elements.push(
          <div key={index} className="flex items-center gap-3 ml-4 my-1">
            <Check size={16} className="text-emerald-400" />
            <span className="text-zinc-400">{line.trim().slice(1).trim()}</span>
          </div>
        );
        return;
      }

      // Regular paragraph
      elements.push(
        <p key={index} className="text-zinc-400 leading-relaxed my-2">
          {renderInlineMarkdown(line)}
        </p>
      );
    });

    return elements;
  };

  // Render inline markdown (bold, links, inline code)
  const renderInlineMarkdown = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Bold text
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Links
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);
      // Inline code
      const codeMatch = remaining.match(/`([^`]+)`/);

      const matches = [
        boldMatch ? { type: 'bold', index: boldMatch.index, match: boldMatch } : null,
        linkMatch ? { type: 'link', index: linkMatch.index, match: linkMatch } : null,
        codeMatch ? { type: 'code', index: codeMatch.index, match: codeMatch } : null
      ].filter(Boolean).sort((a, b) => a.index - b.index);

      if (matches.length === 0) {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }

      const first = matches[0];
      if (first.index > 0) {
        parts.push(<span key={key++}>{remaining.slice(0, first.index)}</span>);
      }

      if (first.type === 'bold') {
        parts.push(<strong key={key++} className="text-white font-semibold">{first.match[1]}</strong>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'link') {
        const isExternal = first.match[2].startsWith('http');
        parts.push(
          <a 
            key={key++} 
            href={first.match[2]} 
            className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
          >
            {first.match[1]}
            {isExternal && <ExternalLink size={12} className="inline ml-1" />}
          </a>
        );
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'code') {
        parts.push(
          <code key={key++} className="px-1.5 py-0.5 bg-zinc-800 rounded text-emerald-400 text-sm font-mono">
            {first.match[1]}
          </code>
        );
        remaining = remaining.slice(first.index + first.match[0].length);
      }
    }

    return parts;
  };

  const activeDocContent = activeDoc ? docContents[activeDoc] : null;

  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {activeDoc ? (
            <div className="animate-fade-in">
              {/* Back button */}
              <button 
                data-testid="docs-back" 
                onClick={() => setActiveDoc(null)} 
                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-emerald-400 transition-colors mb-8 group"
              >
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> 
                Back to Documentation
              </button>

              {/* Article header */}
              <div className="flex items-center gap-4 mb-6">
                {activeDocContent?.icon && (
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <activeDocContent.icon size={24} className="text-emerald-400" />
                  </div>
                )}
                <div>
                  <h1 data-testid="doc-detail-title" className="text-3xl font-bold text-white">
                    {activeDoc}
                  </h1>
                  {activeDocContent?.readTime && (
                    <p className="text-sm text-zinc-500 mt-1">
                      📖 {activeDocContent.readTime} read
                    </p>
                  )}
                </div>
              </div>

              {/* Article content */}
              <article className="bg-[#0A0A0A] border border-zinc-800 rounded-lg p-8 md:p-12">
                {activeDocContent?.content ? (
                  renderContent(activeDocContent.content)
                ) : (
                  <div className="text-center py-12">
                    <FileText size={48} className="text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">
                      Documentation for "{activeDoc}" is being written.
                    </p>
                    <p className="text-zinc-600 text-sm mt-2">
                      Check back soon or contact support for immediate assistance.
                    </p>
                  </div>
                )}
              </article>

              {/* Navigation */}
              <div className="mt-8 flex items-center justify-between">
                <button 
                  onClick={() => setActiveDoc(null)}
                  className="text-sm text-zinc-500 hover:text-emerald-400 transition-colors"
                >
                  ← All Documentation
                </button>
                <a 
                  href="/contact" 
                  className="text-sm text-zinc-500 hover:text-emerald-400 transition-colors"
                >
                  Need help? Contact Support →
                </a>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-12">
                <h1 data-testid="docs-title" className="text-4xl sm:text-5xl font-bold text-white mb-4">
                  Documentation
                </h1>
                <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
                  Everything you need to deploy, configure, and manage your AI agents on ClawStinger.
                </p>
              </div>

              {/* Search */}
              <div className="relative mb-12 max-w-2xl mx-auto">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input 
                  data-testid="docs-search" 
                  type="text" 
                  value={search} 
                  onChange={e => setSearch(e.target.value)} 
                  placeholder="Search documentation..." 
                  className="w-full bg-[#0A0A0A] border border-zinc-800 text-white rounded-lg pl-12 pr-4 py-4 text-sm focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 outline-none placeholder-zinc-600 transition-all" 
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Quick links */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {['Quick Start Guide', 'API Reference', 'RDP Connection Guide', 'Troubleshooting'].map((doc) => {
                  const DocIcon = docContents[doc]?.icon || BookOpen;
                  return (
                    <button
                      key={doc}
                      onClick={() => setActiveDoc(doc)}
                      className="flex flex-col items-center gap-3 p-4 bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-lg hover:border-emerald-500/50 hover:bg-zinc-900 transition-all group"
                    >
                      <DocIcon size={24} className="text-zinc-500 group-hover:text-emerald-400 transition-colors" />
                      <span className="text-sm text-zinc-400 group-hover:text-white transition-colors text-center">{doc}</span>
                    </button>
                  );
                })}
              </div>

              {/* Sections grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filtered.map((section, i) => {
                  const SectionIcon = sectionIcons[section.title] || BookOpen;
                  return (
                    <div 
                      key={i} 
                      data-testid={`docs-section-${i}`} 
                      className="bg-[#0A0A0A] border border-zinc-800 rounded-lg p-6 hover:border-zinc-700 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <SectionIcon size={20} className="text-emerald-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                      </div>
                      <ul className="space-y-1">
                        {section.items.map((item, j) => {
                          const ItemIcon = docContents[item]?.icon || ChevronRight;
                          return (
                            <li key={j}>
                              <button 
                                data-testid={`doc-item-${i}-${j}`} 
                                onClick={() => setActiveDoc(item)} 
                                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md transition-all text-zinc-400 hover:text-white hover:bg-zinc-800/50 group"
                              >
                                <ItemIcon size={16} className="text-zinc-600 group-hover:text-emerald-400 transition-colors" />
                                <span className="flex-1">{item}</span>
                                <ChevronRight size={14} className="text-zinc-700 group-hover:text-zinc-500 group-hover:translate-x-1 transition-all" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Help section */}
              <div className="mt-12 text-center p-8 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border border-zinc-800 rounded-lg">
                <HelpCircle size={32} className="text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Can't find what you're looking for?</h3>
                <p className="text-zinc-400 mb-4">Our support team is here to help you succeed.</p>
                <div className="flex items-center justify-center gap-4">
                  <a href="/contact" className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-black font-medium rounded-lg transition-colors">
                    Contact Support
                  </a>
                  <a href="https://discord.gg/clawstinger" target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-zinc-700 hover:border-zinc-600 text-white rounded-lg transition-colors">
                    Join Discord
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
